import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect } from 'react';
import { COUNTRIES_QUERY } from '@/components/Filters/operations';
import FilterItem from '../../../components/Filters/filterItem';
import { MAP_SOURCE_IDS, MAP_SOURCE_RETRY_TIMEOUT } from '../../../components/Map/Utils/constants';
import { getUserId } from '../../../utils/auth';
import {
  ANALYTICS_EVENT_ACTIONS,
  ANALYTICS_EVENTS,
  OIL_AND_GAS_MAP_SUBSCRIPTION,
} from '../../../utils/constants';
import { getLatLongFromPoint } from '../../../utils/helpers';
import {
  ASSETS_BY_NAME_AND_COUNTRY_QUERY,
  BLOCKS_AND_LEASES_BY_NAME_AND_COUNTRY_QUERY,
  FACILITIES_BY_NAME_AND_COUNTRY_QUERY,
  FIELDS_BY_NAME_AND_COUNTRY_QUERY,
  LNG_PROJECTS_BY_NAME_AND_COUNTRY_QUERY,
  PIPELINE_NETWORKS_BY_NAME_AND_COUNTRY_QUERY,
  WELLS_BY_NAME_AND_COUNTRY_QUERY,
} from './operations';
import MapSearchContext from './searchContext';
import { SearchLabels } from './searchTypes';

const useStyles = makeStyles(theme => ({
  search: {
    ...theme.palette.mapTab,
    ...theme.palette.scrollbar,
  },
  reset: {
    ...theme.palette.button,
    margin: '18px 8px',
  },
}));
// This variable is used to store the search object when the map isn't ready yet
let globalSearchObject = null;

export default ({ map, setCurrentItem, setIsInfoOpen, hidden }) => {
  const classes = useStyles();
  const { setFilter, resetState, state } = useContext(MapSearchContext);

  // Track if the first LNG projects load has happened so we can show the icons:
  const [isLngProjectsFirstLoad, setIsLngProjectsFirstLoad] = React.useState(true);
  const handleLNGProjectsLoaded = data => {
    const { lngProjectsByNameAndCountry: lngProjects } = data;
    if (!isLngProjectsFirstLoad) return;
    if (!map) return;

    const lngFacilitiesSource = map?.getSource(MAP_SOURCE_IDS.lngFacilities);
    if (lngFacilitiesSource) {
      setIsLngProjectsFirstLoad(false);

      const features =
        lngProjects?.map(lngProject => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: getLatLongFromPoint(lngProject.geom),
          },
          properties: {
            id: lngProject.id,
            name: lngProject.name,
            facilityIcon: lngProject.iconName,
            countryIsoCode: lngProject.country.isoCode,
            geom: lngProject.geom,
            entityType: 'lng_project',
            category: lngProject.category,
          },
        })) || [];
      lngFacilitiesSource.setData({
        type: 'FeatureCollection',
        features,
      });
    } else {
      // If the source isn't ready yet, try again in 3 seconds:
      setTimeout(() => {
        handleLNGProjectsLoaded(data);
      }, MAP_SOURCE_RETRY_TIMEOUT);
    }

    map.on('style.load', () => {
      // reload lngFacilities when map style changes
      handleLNGProjectsLoaded(data);
    });
  };

  const resetSearch = () => {
    resetState();
    setCurrentItem(null);
    setIsInfoOpen(false);
    map?.resetZoom();
  };

  const handleSelect = (key, value, sourceLayerOverride) => {
    // Check if style is loaded
    if (map?.style?._loaded) {
      setFilter(key, value);
      if (value === null) return;
      const dimensions = { asset: 'NULL', country: 'NULL', company: 'NULL' };
      switch (key) {
        case 'assets':
        case 'blocks':
          dimensions['asset'] = value.name;
          dimensions['country'] = value.country.name;
          break;
        case 'country':
          dimensions['country'] = value.name;
          break;
      }
      gtag('event', ANALYTICS_EVENTS.event, {
        event_category: 'Maps',
        event_action: ANALYTICS_EVENT_ACTIONS.search,
        event_label: JSON.stringify({ [key]: value }),
        userId: getUserId(),
        ...dimensions,
      });

      if (key !== 'country') {
        const clickedFeature = map.createClickedFeatureForSearch(key, value);
        setCurrentItem({
          properties: clickedFeature,
          sourceLayer: sourceLayerOverride || key,
          layerCountry: value.country?.isoCode,
        });
        setIsInfoOpen(true);
      }

      map.flyTo({
        center: getLatLongFromPoint(value.geom),
        zoom:
          key === 'country'
            ? value.zoom
            : value.country?.isoCode !== 'USA' && (key === 'assets' || key === 'blocks')
              ? 9
              : 13,
      });
    } else {
      // If the map style isn't ready yet, save the search object to be used later
      globalSearchObject = {
        key,
        value,
        sourceLayerOverride,
      };
    }
  };

  // This use effect waits for the map to be loaded. When the map loads and there is a search object object present, it will:
  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    // Check if the search object and map are loaded
    if (globalSearchObject && map) {
      const { key, value, sourceLayerOverride } = globalSearchObject;
      // If the map has loaded, check if the style loaded again... If so, call handleSelect
      if (map?.style?._loaded) {
        handleSelect(key, value, sourceLayerOverride);
      } else {
        // If the style still hasn't loaded, add the handleSelect method to the style.load event listener
        // so we can handle panning/zooming once the map style is loaded
        map.on('style.load', () => handleSelect(key, value, sourceLayerOverride));
      }

      globalSearchObject = null;
    }
  }, [map, globalSearchObject]);

  const filterItemClass = '';
  return (
    !hidden && (
      <div className={classes.search}>
        <Button variant='contained' className={classes.reset} onClick={resetSearch}>
          reset search
        </Button>
        <Divider style={{ marginBottom: '15px' }} />
        <FilterItem
          context={`asset-og-basic-filters`}
          filter={{
            query: COUNTRIES_QUERY,
            name: 'Go to Country/Area',
            optionKey: 'displayName',
            runQueryAfterOnChange: false,
            queryVariable: null,
            multiple: false,
            menuItems: null,
          }}
          setFilter={value => handleSelect('country', value)}
          value={state.country}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          id='country-search'
          globalSearchType={'COUNTRY'}
        />
        <FilterItem
          context={`asset-og-basic-filters`}
          filter={{
            query: ASSETS_BY_NAME_AND_COUNTRY_QUERY,
            name: SearchLabels.asset,
            optionKey: 'menuName',
            runQueryAfterOnChange: true,
            queryVariable: 'search',
            multiple: false,
            menuItems: null,
          }}
          setFilter={value => handleSelect('assets', value)}
          propVariables={{
            country: state.country ? state.country.id : null,
            feature: OIL_AND_GAS_MAP_SUBSCRIPTION,
          }}
          value={state.assets}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          id='asset-search'
          globalSearchType={'ASSET'}
        />
        <FilterItem
          context={`blocks-og-basic-filters`}
          filter={{
            query: BLOCKS_AND_LEASES_BY_NAME_AND_COUNTRY_QUERY,
            name: SearchLabels.block,
            optionKey: 'menuName',
            runQueryAfterOnChange: true,
            queryVariable: 'search',
            multiple: false,
            menuItems: null,
          }}
          setFilter={value =>
            handleSelect('blocks', value, value?.legacyLeaseId !== null && 'leases')
          }
          propVariables={{
            country: state.country ? state.country.id : null,
            feature: OIL_AND_GAS_MAP_SUBSCRIPTION,
          }}
          value={state.blocks}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          globalSearchType={'BLOCK'}
        />
        <FilterItem
          context={`fields-og-basic-filters`}
          filter={{
            query: FIELDS_BY_NAME_AND_COUNTRY_QUERY,
            name: SearchLabels.field,
            optionKey: 'menuName',
            runQueryAfterOnChange: true,
            queryVariable: 'search',
            multiple: false,
            menuItems: null,
          }}
          setFilter={value => handleSelect('fields', value)}
          propVariables={{
            country: state.country ? state.country.id : null,
            feature: OIL_AND_GAS_MAP_SUBSCRIPTION,
          }}
          value={state.fields}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          globalSearchType={'FIELD'}
        />
        <FilterItem
          context={`wells-og-basic-filters`}
          filter={{
            query: WELLS_BY_NAME_AND_COUNTRY_QUERY,
            name: SearchLabels.well,
            optionKey: 'menuName',
            runQueryAfterOnChange: true,
            queryVariable: 'search',
            multiple: false,
            menuItems: null,
          }}
          setFilter={value => handleSelect('wells', value)}
          propVariables={{
            country: state.country ? state.country.id : null,
            feature: OIL_AND_GAS_MAP_SUBSCRIPTION,
          }}
          value={state.wells}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          globalSearchType={'WELL'}
        />
        <FilterItem
          context={`facilities-og-basic-filters`}
          filter={{
            query: FACILITIES_BY_NAME_AND_COUNTRY_QUERY,
            name: SearchLabels.facility,
            optionKey: 'menuName',
            runQueryAfterOnChange: true,
            queryVariable: 'search',
            multiple: false,
            menuItems: null,
          }}
          setFilter={value => handleSelect('facilities', value)}
          propVariables={{
            country: state.country ? state.country.id : null,
            feature: OIL_AND_GAS_MAP_SUBSCRIPTION,
          }}
          value={state.facilities}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          globalSearchType={'FACILITY'}
        />
        <FilterItem
          context={`lng-og-basic-filters`}
          filter={{
            query: LNG_PROJECTS_BY_NAME_AND_COUNTRY_QUERY,
            name: SearchLabels.lngFacility,
            optionKey: 'menuName',
            runQueryAfterOnChange: true,
            queryVariable: 'search',
            multiple: false,
            menuItems: null,
          }}
          onDataLoaded={handleLNGProjectsLoaded}
          setFilter={value => handleSelect('lngProjects', value, 'lng')}
          propVariables={{
            country: state.country ? state.country.id : null,
            feature: OIL_AND_GAS_MAP_SUBSCRIPTION,
          }}
          value={state.lngProjects}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          globalSearchType={'LNG_PROJECT'}
        />
        <FilterItem
          context={`pipeline-og-basic-filters`}
          filter={{
            query: PIPELINE_NETWORKS_BY_NAME_AND_COUNTRY_QUERY,
            name: SearchLabels.pipelineNetwork,
            optionKey: 'name',
            runQueryAfterOnChange: true,
            queryVariable: 'search',
            multiple: false,
            menuItems: null,
          }}
          setFilter={value => handleSelect('pipelineNetworks', value)}
          propVariables={{
            country: state.country ? state.country.id : null,
            feature: OIL_AND_GAS_MAP_SUBSCRIPTION,
          }}
          value={state.pipelineNetworks}
          disabled={false}
          className={filterItemClass}
          inModal={false}
          globalSearchType={'PIPELINE_NETWORK'}
        />
      </div>
    )
  );
};
